import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { IAccount } from "@app/services/account.service";
import { DataCacheService } from "@app/services/caching/data-cache.service";
import { DateService, DateUnit } from "@app/services/date.service";
import { combineLatest, Observable, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { EngineerJobStatus, IEngineerJob, IEngineerJobBooking, IEngineerJobBookingWithEngineerMappings } from "../foundation.interfaces";
import { EngineerJobBookingService } from "./engineer-job-booking.service";

@Injectable({
    providedIn: 'root'
})
export class EngineerJobService {
    private jobsCacheName: string = "engineerJobsCache";
    
    constructor(private readonly http: HttpClient,
        private readonly dataCacheService: DataCacheService,
        private readonly dateService: DateService,
        private readonly engineerJobBookingService: EngineerJobBookingService) { }

    getEngineerJob(jobId: number): Observable<IEngineerJob> {
        return combineLatest([
            this.getEngineerJobById(jobId),
            this.engineerJobBookingService.getEngineeringJobBookingsWithMappings({ JobId: jobId })
        ]).pipe(map(([job, bookings]) => {
            job.jobBooking = bookings[0];

            return job;
        }))
    }

    getEngineerJobs(invalidateCache = false): Observable<IEngineerJob[]> {
        return this.dataCacheService.getOrRequest(this.jobsCacheName, () => this.getEngineerJobsFromDb(), { invalidateCache })
    }

    getEngineerJobsFromDb(): Observable<IEngineerJob[]> {
        return this.http.get<IEngineerJob[]>(`/api/foundation/engineerJobs/`);
    }

    getEngineerJobsForAccount(accountId: number): Observable<IEngineerJob[]> {
        return this.http.get<IEngineerJob[]>(`/api/foundation/engineerJobs/account/${accountId}`);
    }

    getEngineerJobById(jobId: number): Observable<IEngineerJob> {
        const cachedJob = this.dataCacheService.getSingleValue(jobId, this.jobsCacheName);

        if (!!cachedJob) {
            return of(cachedJob);
        }

        return this.http.get<IEngineerJob>(`/api/foundation/engineerJobs/${jobId}`)
    }

    createUpdateEngineerJob(engineerJob: IEngineerJob): Observable<IEngineerJob> {
        return this.http.post<IEngineerJob>(`/api/foundation/engineerJobs/`, engineerJob).pipe(tap(job => {
            this.dataCacheService.addOrUpdateItem(job, this.jobsCacheName);
        }));
    }

    getEngineeringJobBookings(dateFrom: Date, dateTo: Date): Observable<IEngineerJobBooking[]> {
        const params = {
            from: this.dateService.getStartOfDay(dateFrom).toISOString(),
            to: this.dateService.getEndOfDay(dateTo).toISOString()
        }

        return this.http.get<IEngineerJobBooking[]>(`/api/foundation/engineers/bookings/`, { params });

    }

    getEngineeringJobBookingsById(jobId: number): Observable<IEngineerJobBooking[]> {
        return this.http.get<IEngineerJobBooking[]>(`/api/foundation/engineerbookings/jobId/${jobId}`);
    }

    closeJob(jobId: number): Observable<IEngineerJob> {
        const job = this.dataCacheService.getSingleValue(jobId, this.jobsCacheName);
        job.Status = EngineerJobStatus.Completed;

        return this.createUpdateEngineerJob(job);
    }

    mapAccountsToJobs(jobs: IEngineerJob[], accounts: IAccount[]): IEngineerJob[] {
        if (!jobs?.length || !accounts?.length) {
            return;
        }

        jobs.forEach(job => {
            job.account = accounts.find(account => account.Id === job.AccountId);
        });

        return jobs;
    }

    mapBookingsToJobs(jobs: IEngineerJob[], bookings: IEngineerJobBookingWithEngineerMappings[]): void  {
        if (!jobs?.length || !bookings?.length) {
            return;
        }

        jobs.forEach(job => {
            job.jobBooking = bookings.find(booking => booking.JobId === job.Id);
        })
    }

    getCompanyNameForJob(job: IEngineerJob, accounts: IAccount[]): string {
        const account = accounts.find(account => account.Id === job.AccountId);
        if (!account) {
            return;
        }

        return account.CompanyName;
    }


    jobIsClosed(job: IEngineerJob): boolean {
        if (job.Status === EngineerJobStatus.Cancelled || job.Status === EngineerJobStatus.Completed) {
            return true;
        }
    }

    createNewJob(): IEngineerJob {
        return {
            Id: 0,
            Status: EngineerJobStatus.Open,
            CreatedDate: new Date(),
            Description: "",
            CustomerInstruction: "",
            EngineerInstruction: "",
            AccountId: null,
            DealId: null,
            jobBooking: {
                StartUtc: new Date(),
                EndUtc: this.dateService.addOrSubtractTime(new Date(), 7, DateUnit.Day),
            }
        }
    }
}
